import { useTranslation } from 'next-i18next';
import { useMemo } from 'react';
import {
  formatI18nDate,
  formatI18nDateWithTemplate,
  formatI18nTime,
  getUserTimezone,
  type DateFormatType,
} from 'utils/i18n-format-date';
import {
  formatI18nCurrency,
  formatI18nNumber,
  formatI18nCompactPrice,
  parseI18nCurrency,
  getCurrencyFromLocale,
  type SupportedCurrency,
} from 'utils/i18n-format-currency';

/**
 * Custom hook that provides locale-aware formatting functions for dates, times, currency, and numbers.
 * Automatically uses the current i18n locale from next-i18next context.
 * 
 * @returns Object containing formatting functions and locale information
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { formatDate, formatCurrency, locale } = useI18nFormatting();
 *   
 *   return (
 *     <div>
 *       <p>{formatDate(new Date(), 'long')}</p>
 *       <p>{formatCurrency(1234567)}</p>
 *     </div>
 *   );
 * }
 * ```
 */
export function useI18nFormatting() {
  const { i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const currency = useMemo(() => getCurrencyFromLocale(locale), [locale]);
  const timezone = useMemo(() => getUserTimezone(), []);

  return useMemo(() => ({
    // Locale information
    locale,
    currency,
    timezone,
    
    // Date formatting functions
    /**
     * Formats a date according to the specified format type.
     * 
     * @param date - The date to format
     * @param format - The format type: 'short', 'long', 'relative', or 'custom'
     * @param customFormat - Optional custom format string (used when format is 'custom')
     * @returns Formatted date string
     * 
     * @example
     * formatDate(new Date(), 'long') // "October 15, 2025 at 2:30 PM" (en-CA)
     * formatDate(new Date(), 'short') // "October 15, 2025"
     * formatDate(new Date(), 'relative') // "2 hours ago"
     * formatDate('2026-06-09', 'monthDay') // "Jun 9th" (en) or "9 juin" (fr)
     */
    formatDate: (
      date: Date | string | number,
      format: DateFormatType = 'long',
      customFormat?: string
    ) => formatI18nDate(date, format, locale, customFormat),
    
    /**
     * Formats a date using a custom dayjs template.
     * 
     * @param date - The date to format
     * @param template - The dayjs format template (e.g., 'MMM DD, YYYY')
     * @returns Formatted date string
     * 
     * @example
     * formatDateWithTemplate(new Date(), 'MMM DD, YYYY') // "Oct 15, 2025"
     * formatDateWithTemplate(new Date(), 'ddd. MMM D') // "Wed. Oct 15"
     */
    formatDateWithTemplate: (date: Date | string | number, template: string) =>
      formatI18nDateWithTemplate(date, template, locale),

    /**
     * Formats time only (no date).
     * 
     * @param date - The date/time to format
     * @param includeSeconds - Whether to include seconds in the output
     * @returns Formatted time string
     * 
     * @example
     * formatTime(new Date()) // "2:30 PM" (en-CA) or "14:30" (fr-CA)
     */
    formatTime: (date: Date | string | number, includeSeconds?: boolean) =>
      formatI18nTime(date, locale, includeSeconds),
    
    // Currency formatting functions
    /**
     * Formats a number as currency.
     * Automatically uses the appropriate currency (CAD for Canadian locales, USD otherwise).
     * 
     * @param value - The numeric value to format
     * @param options - Additional formatting options
     * @returns Formatted currency string
     * 
     * @example
     * formatCurrency(1234567) // "CA$1,234,567" (en-CA) or "1 234 567 $ CA" (fr-CA)
     */
    formatCurrency: (
      value: number,
      options?: {
        currency?: SupportedCurrency;
        minimumFractionDigits?: number;
        maximumFractionDigits?: number;
      }
    ) => formatI18nCurrency(value, locale, options?.currency || currency, options),
    
    /**
     * Formats a number with locale-specific thousand separators.
     * 
     * @param value - The numeric value to format
     * @param options - Additional formatting options
     * @returns Formatted number string
     * 
     * @example
     * formatNumber(1234567) // "1,234,567" (en-CA) or "1 234 567" (fr-CA)
     */
    formatNumber: (
      value: number,
      options?: {
        minimumFractionDigits?: number;
        maximumFractionDigits?: number;
      }
    ) => formatI18nNumber(value, locale, options),

    /**
     * Formats a price using compact notation (e.g., 1.5M, 500K).
     * 
     * @param value - The numeric value to format
     * @returns Formatted compact price string
     * 
     * @example
     * formatCompactPrice(1500000) // "CA$1.5M" (en-CA) or "1,5 M$ CA" (fr-CA)
     * formatCompactPrice(500000) // "CA$500K" (en-CA)
     */
    formatCompactPrice: (value: number) => formatI18nCompactPrice(value, locale, currency),

    /**
     * Parses a formatted currency string back to a number.
     * 
     * @param formattedValue - The formatted currency string
     * @returns Numeric value or null if invalid
     * 
     * @example
     * parseCurrency("CA$1,234,567") // 1234567
     */
    parseCurrency: (formattedValue: string) => parseI18nCurrency(formattedValue),
  }), [locale, currency, timezone]);
}
